import React, { memo, useEffect, useMemo } from 'react';

import { Controller, UseFormReturn } from 'react-hook-form';

import { useDebounce } from 'hooks/useDebaunce';

import { useQueryGetMilestoneFragnet } from 'api/queries/getMilestoneFragnet.query';
import { useQueryGetSingleMilestoneFragnet } from 'api/queries/getSingleMilestoneFragnet.query';

import { FiltersStatus } from 'components/common/GlobalFiltersInPage/GlobalFiltersInPage';
import classes from 'components/common/GlobalFiltersInPage/GlobalFiltersInPage.module.scss';
import { FilterLabels } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';
import { signalSingleFilterIsReady } from 'components/common/GlobalFiltersInPage/hooks/useSubscribersFiltersReady';

import { milestoneFragnetMapper } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/helper';
import { Select } from 'components/DesignSystem/Select';

interface Props {
    methods?: UseFormReturn<any, any>;
    name: string;
    filtersStatusRef?: React.MutableRefObject<FiltersStatus>;
    handleFilters?: () => void;
    versionId?: number | undefined;
    value?: string;
    isSelected?: boolean;
}

export const MilestoneFragnetFilter = memo(
    ({ methods, name, filtersStatusRef, handleFilters, versionId, value, isSelected = true }: Props) => {
        const [filter, setFilter] = React.useState('');
        const debouncedValue = useDebounce<string>(filter, 1000);
        const {
            data: milestoneFragnet,
            isFetching,
            isSuccess,
        } = useQueryGetMilestoneFragnet({
            latestVersion: versionId,
            filter: debouncedValue,
        });

        const options = useMemo<ISelectOption<string>[]>(
            () =>
                milestoneFragnet
                    ? milestoneFragnet.map((item) => ({
                          id: item.id,
                          label: `${item.name} (ID: ${
                              item.activity_id.length > 20 ? item.activity_id.slice(0, 20) + '...' : item.activity_id
                          }) (~${item.preceding_activities} related activities)`,
                          value: item.task_version_hash_code,
                      }))
                    : [],
            [milestoneFragnet],
        );

        const { data: milestoneSingleFragnet } = useQueryGetSingleMilestoneFragnet({
            latestVersion: versionId,
            taskVersionHashCode: value, // make sure BE can perform search by task_version_hash_code
        });

        useEffect(() => {
            if (!filtersStatusRef?.current || filtersStatusRef?.current[name]?.isReady || !isSuccess) {
                return;
            }

            if (milestoneSingleFragnet && milestoneSingleFragnet.length > 0) {
                methods?.setValue(name, milestoneFragnetMapper(milestoneSingleFragnet[0]).value);
            }

            signalSingleFilterIsReady({ filterId: name, isReady: isSuccess });
        }, [value, milestoneFragnet, milestoneSingleFragnet, filtersStatusRef, isSuccess, name, methods]);

        const handleInputChangePrimary = (inputText, event) => {
            if (event.action !== 'input-blur' && event.action !== 'menu-close' && event.action !== 'set-value') {
                setFilter(inputText);
            }
            if (event.action === 'menu-close') {
                setFilter('');
            }
        };

        const handleValue = (value) => {
            if (!value) {
                return null;
            }
            if (
                milestoneSingleFragnet &&
                milestoneSingleFragnet.length > 0 &&
                milestoneFragnetMapper(milestoneSingleFragnet[0]).value === value
            ) {
                return milestoneFragnetMapper(milestoneSingleFragnet[0]);
            }
            return milestoneFragnet?.map(milestoneFragnetMapper).find((item) => item.value === value) || null;
        };

        return isSelected ? (
            <div className={classes.selectWrapper}>
                <div data-testid={'battlecards-filters-fragnet'} className={classes.activityCodesSelectWrapper}>
                    <Controller
                        render={({ field }) => (
                            <Select<ISelectOption<string>>
                                {...field}
                                options={options}
                                id={'global-filter-component-fragnet-milestones'}
                                onInputChange={handleInputChangePrimary}
                                isClearable={true}
                                isLoading={isFetching}
                                onChange={async (newValue) => {
                                    methods?.setValue(name, newValue ? newValue.value : null);
                                    await methods?.trigger('completionPlanSegment');
                                    const isValid = await methods?.trigger('activityForwardFragnet');
                                    if (isValid) {
                                        handleFilters && handleFilters();
                                    }
                                }}
                                value={handleValue(field.value)}
                                placeholder={'Select activity or start typing its name...'}
                                label={FilterLabels.FRAGNET_SEARCH}
                            />
                        )}
                        name={name}
                        control={methods?.control}
                    />
                </div>
            </div>
        ) : null;
    },
);

MilestoneFragnetFilter.displayName = 'MilestoneFragnetFilter';
